/**
 * /login - the sign-in page (CHAP-284 magic link; CHAP-490 adds Google,
 * Microsoft and email/password per Jake's recorded ruling, replicating the
 * app.immigr8.net / app.1116.info flow).
 *
 * Provider order is fixed and identical everywhere this surface renders:
 * Google, Microsoft, email/password, then the magic link. Buttons appear only
 * for providers the server reports live (/api/auth/providers), so a dormant
 * provider is invisible rather than broken.
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiGet, apiPost } from '../lib/api.js';
import { returnPathFromQuery, RETURN_TO_KEY } from '../../shared/return-path.js';

interface Providers {
  magicLink: boolean;
  password: boolean;
  google: boolean;
  microsoft: boolean;
}

export default function Login() {
  const [params] = useSearchParams();
  const returnTo = params.get('returnTo') ?? '';
  // CHAP-594 review: after sign-in, returnTo is followed only when it is a
  // same-origin path (shared/return-path.ts); otherwise the default page.
  const safeReturn = returnPathFromQuery(returnTo);
  const oidcError = params.get('error') ?? '';
  const [providers, setProviders] = useState<Providers>({
    magicLink: true,
    password: true,
    google: false,
    microsoft: false,
  });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Magic link is the default until members have enrolled passwords: today zero
  // rows carry a hash, so a password-first form would greet everyone with a
  // credential they do not have. The e2e cabinet journey drives this default.
  const [mode, setMode] = useState<'password' | 'magic'>('magic');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loginError, setLoginError] = useState('');

  useEffect(() => {
    apiGet('/api/auth/providers')
      .then((p) => setProviders(p as Providers))
      .catch(() => {
        // Endpoint unreachable: fall back to the always-on methods.
      });
  }, []);

  function oidcStart(provider: 'google' | 'microsoft') {
    const q = returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : '';
    window.location.href = `/api/auth/oidc/${provider}/start${q}`;
  }

  async function submitPassword(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setBusy(true);
    setLoginError('');
    try {
      await apiPost('/api/auth/password/redeem', { email: email.trim(), password });
      window.location.href = safeReturn ?? '/';
      return;
    } catch {
      setLoginError('That email and password did not match.');
    }
    setBusy(false);
  }

  async function submitMagic(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      if (safeReturn) localStorage.setItem(RETURN_TO_KEY, safeReturn);
      await apiPost('/api/auth/magic-link/request', { email: email.trim() });
    } catch {
      // Always show success (enumeration protection).
    }
    setSent(true);
    setBusy(false);
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-sm bg-white rounded-xl shadow-sm border border-gray-100 p-6 sm:p-8">
        <h1 className="text-navy text-xl font-bold">Sign in to ChapterBuild</h1>

        {oidcError === 'link_required' && (
          <p className="mt-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            We could not confirm that this sign-in belongs to an existing member, so we did
            not connect it. Use the email link below to sign in.
          </p>
        )}
        {oidcError && oidcError !== 'link_required' && (
          <p className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
            That sign-in did not complete. Try again, or use an email link below.
          </p>
        )}

        {(providers.google || providers.microsoft) && (
          <div className="mt-4 space-y-2">
            {providers.google && (
              <button
                type="button"
                onClick={() => oidcStart('google')}
                className="w-full px-4 py-2 rounded border border-gray-300 text-sm font-medium text-gray-800 bg-white hover:bg-gray-50 flex items-center justify-center gap-2"
              >
                <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden="true">
                  <path fill="currentColor" d="M21.6 12.2c0-.7-.1-1.4-.2-2H12v3.9h5.4a4.6 4.6 0 0 1-2 3v2.6h3.2c1.9-1.8 3-4.4 3-7.5Z" />
                  <path fill="currentColor" d="M12 22c2.7 0 5-.9 6.6-2.4l-3.2-2.5c-.9.6-2 1-3.4 1-2.6 0-4.8-1.8-5.6-4.1H3.1v2.6A10 10 0 0 0 12 22Z" opacity=".8" />
                  <path fill="currentColor" d="M6.4 14a6 6 0 0 1 0-3.9V7.5H3.1a10 10 0 0 0 0 9l3.3-2.6Z" opacity=".6" />
                  <path fill="currentColor" d="M12 6c1.5 0 2.8.5 3.8 1.5L18.6 5A10 10 0 0 0 3.1 7.5l3.3 2.6C7.2 7.8 9.4 6 12 6Z" opacity=".7" />
                </svg>
                Continue with Google
              </button>
            )}
            {providers.microsoft && (
              <button
                type="button"
                onClick={() => oidcStart('microsoft')}
                className="w-full px-4 py-2 rounded border border-gray-300 text-sm font-medium text-gray-800 bg-white hover:bg-gray-50 flex items-center justify-center gap-2"
              >
                <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden="true">
                  <path fill="currentColor" d="M3 3h8.5v8.5H3V3Zm9.5 0H21v8.5h-8.5V3ZM3 12.5h8.5V21H3v-8.5Zm9.5 0H21V21h-8.5v-8.5Z" />
                </svg>
                Continue with Microsoft
              </button>
            )}
            <div className="flex items-center gap-3 py-1">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-[11px] uppercase tracking-wide text-gray-400">or</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>
          </div>
        )}

        {sent ? (
          <div className="mt-4 text-sm text-gray-700 space-y-2">
            <p>
              If <strong>{email}</strong> has access, a sign-in link is on its way.
            </p>
            <p className="text-neutral">
              Check your email and click the link to continue. The link is valid for 30 minutes.
            </p>
            <button onClick={() => setSent(false)} className="text-blue-600 text-sm underline">
              Use a different email
            </button>
          </div>
        ) : mode === 'password' ? (
          <form onSubmit={submitPassword} className="mt-4 space-y-3">
            <input
              type="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@chapter.org"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            />
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            />
            {loginError && <p className="text-sm text-red-600">{loginError}</p>}
            <button
              type="submit"
              disabled={busy || !email.trim() || !password}
              className="w-full px-4 py-2 rounded bg-navy text-white text-sm font-medium disabled:opacity-50"
            >
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
            <p className="text-neutral text-xs">
              No password yet, or forgot it?{' '}
              <button type="button" onClick={() => setMode('magic')} className="text-blue-600 underline">
                Email me a sign-in link
              </button>{' '}
              - once you are in, you can set a password from your account.
            </p>
          </form>
        ) : (
          <form onSubmit={submitMagic} className="mt-4 space-y-3">
            <p className="text-neutral text-sm">Enter your email and we'll send you a one-time sign-in link.</p>
            <input
              type="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@chapter.org"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            />
            {returnTo && <input type="hidden" value={returnTo} readOnly />}
            <button
              type="submit"
              disabled={busy || !email.trim()}
              className="w-full px-4 py-2 rounded bg-navy text-white text-sm font-medium disabled:opacity-50"
            >
              {busy ? 'Sending…' : 'Email me a sign-in link'}
            </button>
            <p className="text-neutral text-xs">
              Have a password?{' '}
              <button type="button" onClick={() => setMode('password')} className="text-blue-600 underline">
                Sign in with it instead
              </button>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
